import { Box, MessageBlock, Paragraph } from '@deckee/deck-hand';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { setWeatherForecast } from '../../../../services/store/modules/app-controls';
import { selectCurrentLocation } from '../../../../services/store/modules/geo-service';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import DayStrip from '../components/DayStrip/DayStrip';
import DaySwipePager from '../components/DaySwipePager/DaySwipePager';
import ExtendedForecastCtaCard from '../components/ExtendedForecastCta/ExtendedForecastCtaCard';
import LocationSelector from '../components/LocationSelector/LocationSelector';
import { trackWeatherForecastLocationSearchClick } from '../helpers/analytics/weatherForecastAnalytics';
import { getWeatherForecastDayFromSearch } from '../helpers/day/dayQuery';
import { isExtendedForecastCtaDay } from '../helpers/forecastHorizon';
import { parseCoordinate } from '../helpers/location/favouriteIdentity';
import {
  getWeatherForecastHomePath,
  getWeatherForecastLocationSearchPath,
} from '../helpers/location/searchPaths';
import { useExtendedWeatherForecast } from '../hooks/useExtendedWeatherForecast';
import { useWeatherForecastMoonPhases } from '../hooks/useWeatherForecastMoonPhases';
import { useWeatherForecastOutlook } from '../hooks/useWeatherForecastOutlook';
import { useWeatherForecastSelectedDay } from '../hooks/useWeatherForecastSelectedDay';
import { useWeatherReferenceLocation } from '../hooks/useWeatherReferenceLocation';
import WeatherForecastDayContent from './WeatherForecastDayContent';

const WeatherForecastPageLayout = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const WeatherForecastFixedHeader = styled(Box)`
  flex-shrink: 0;
  z-index: 1;
  box-shadow:
    0 1px 6px rgba(0, 0, 0, 0.07),
    0 0 3px rgba(0, 0, 0, 0.07);
  background: ${({ theme }) => theme.colors.white};
`;

const WeatherForecastLoading: FC = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    py={4}
    data-testid="weather-forecast-loading"
    aria-busy
  >
    <LogoSpinner background={false} />
  </Box>
);

const areValidWeatherCoordinates = (
  latitude: number | undefined,
  longitude: number | undefined,
) =>
  latitude != null &&
  longitude != null &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude);

const WeatherForecastHomePage: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { latitude: routeLatitude, longitude: routeLongitude } = useParams<{
    latitude?: string;
    longitude?: string;
  }>();
  const { latitude: currentLatitude, longitude: currentLongitude } =
    useSelector(selectCurrentLocation);
  const dayFromSearch = getWeatherForecastDayFromSearch(search);

  const routeLatitudeValue = parseCoordinate(routeLatitude);
  const routeLongitudeValue = parseCoordinate(routeLongitude);
  const hasValidRouteCoordinates =
    routeLatitudeValue != null && routeLongitudeValue != null;

  const latitude = useMemo(() => {
    if (hasValidRouteCoordinates) {
      return routeLatitudeValue ?? undefined;
    }

    return currentLatitude ?? undefined;
  }, [currentLatitude, hasValidRouteCoordinates, routeLatitudeValue]);

  const longitude = useMemo(() => {
    if (hasValidRouteCoordinates) {
      return routeLongitudeValue ?? undefined;
    }

    return currentLongitude ?? undefined;
  }, [currentLongitude, hasValidRouteCoordinates, routeLongitudeValue]);

  const coordinatesAreValid = areValidWeatherCoordinates(latitude, longitude);
  const { forecastDaysRequested, showNonSubscriberCtas } =
    useExtendedWeatherForecast();

  const { location, isLoading, loadError } = useWeatherReferenceLocation(
    latitude,
    longitude,
  );

  const {
    forecastDays,
    timeZone,
    isLoading: isOutlookLoading,
    error: outlookError,
  } = useWeatherForecastOutlook(latitude, longitude, forecastDaysRequested);

  const {
    moonPhaseDays,
    isLoading: isMoonPhasesLoading,
    error: moonPhasesError,
  } = useWeatherForecastMoonPhases(latitude, longitude, forecastDaysRequested);

  const {
    selectedDayIndex,
    selectedDate,
    selectedForecastDay,
    defaultDate,
    setSelectedDay,
    selectCtaPage,
  } = useWeatherForecastSelectedDay(forecastDays, {
    ctaPageEnabled: showNonSubscriberCtas,
  });

  const forecastDayDates = useMemo(
    () => new Set(forecastDays.map((day) => day.date)),
    [forecastDays],
  );

  const alignedMoonPhaseDays = useMemo(
    () => moonPhaseDays.filter((day) => forecastDayDates.has(day.date)),
    [forecastDayDates, moonPhaseDays],
  );

  useEffect(() => {
    dispatch(setWeatherForecast());
  }, [dispatch]);

  useEffect(() => {
    if (
      !hasValidRouteCoordinates &&
      currentLatitude != null &&
      currentLongitude != null
    ) {
      history.replace(
        getWeatherForecastHomePath(
          currentLatitude,
          currentLongitude,
          dayFromSearch ?? selectedDate,
          defaultDate,
        ),
      );
    }
  }, [
    currentLatitude,
    currentLongitude,
    dayFromSearch,
    defaultDate,
    hasValidRouteCoordinates,
    history,
    selectedDate,
  ]);

  const openWeatherSearch = useCallback(() => {
    if (!coordinatesAreValid) {
      return;
    }

    trackWeatherForecastLocationSearchClick();

    history.push(
      getWeatherForecastLocationSearchPath(
        latitude,
        longitude,
        undefined,
        selectedDate,
        defaultDate,
      ),
    );
  }, [
    coordinatesAreValid,
    defaultDate,
    history,
    latitude,
    longitude,
    selectedDate,
  ]);

  const handleSwipeIndexChange = useCallback(
    (nextIndex: number) => {
      if (showNonSubscriberCtas && nextIndex === forecastDays.length) {
        selectCtaPage();
        return true;
      }

      const nextDay = forecastDays[nextIndex];
      if (!nextDay) {
        return false;
      }
      setSelectedDay(nextDay.date, nextDay.dayLabel, 'swipe');
      return true;
    },
    [forecastDays, selectCtaPage, setSelectedDay, showNonSubscriberCtas],
  );

  const renderSwipePage = useCallback(
    (pageIndex: number | null, { isFocused }: { isFocused: boolean }) => {
      if (
        pageIndex == null ||
        !coordinatesAreValid ||
        latitude == null ||
        longitude == null
      ) {
        return null;
      }

      if (showNonSubscriberCtas && pageIndex === forecastDays.length) {
        return (
          <LayoutBody>
            <ExtendedForecastCtaCard />
          </LayoutBody>
        );
      }

      const forecastDay = forecastDays[pageIndex];
      if (!forecastDay) {
        return null;
      }

      return (
        <LayoutBody>
          <WeatherForecastDayContent
            latitude={latitude}
            longitude={longitude}
            selectedDate={forecastDay.date}
            selectedForecastDay={forecastDay}
            timeZone={timeZone}
            forecastDays={forecastDays}
            moonPhaseDays={alignedMoonPhaseDays}
            moonPhasesLoading={isMoonPhasesLoading}
            moonPhasesError={moonPhasesError}
            onSelectDay={setSelectedDay}
            forecastDaysRequested={forecastDaysRequested}
            isActive={isFocused}
          />
        </LayoutBody>
      );
    },
    [
      alignedMoonPhaseDays,
      coordinatesAreValid,
      forecastDays,
      forecastDaysRequested,
      isMoonPhasesLoading,
      latitude,
      longitude,
      moonPhasesError,
      setSelectedDay,
      showNonSubscriberCtas,
      timeZone,
    ],
  );

  const isLocationLoading =
    coordinatesAreValid && !loadError && (isLoading || !location?.name);

  const isForecastContentLoading =
    coordinatesAreValid && !loadError && isOutlookLoading;

  const hasOutlookDays = forecastDays.length > 0;
  const isCtaPageSelected = isExtendedForecastCtaDay(selectedDate);
  const showDaySwipe =
    Boolean(
      (selectedForecastDay || isCtaPageSelected) &&
      coordinatesAreValid &&
      selectedDate,
    ) &&
    hasOutlookDays &&
    !isForecastContentLoading &&
    !(outlookError && !hasOutlookDays);
  const swipePageCount = forecastDays.length + (showNonSubscriberCtas ? 1 : 0);

  return (
    <BasicDrawerLayout showHeaderBar={false} showMenuBar>
      <WeatherForecastPageLayout>
        <WeatherForecastFixedHeader px={2} pt={2} pb={2}>
          <LocationSelector
            location={location}
            isLoading={isLocationLoading}
            onSelectLocation={openWeatherSearch}
            compact
          />

          <Box pt={1}>
            <DayStrip
              forecastDays={forecastDays}
              selectedDate={selectedDate}
              onSelectDay={setSelectedDay}
              isLoading={isOutlookLoading}
              error={outlookError}
              compact
              showExtendedForecastCta={showNonSubscriberCtas}
              loadingDayCount={forecastDaysRequested}
            />
          </Box>
        </WeatherForecastFixedHeader>

        {loadError ? (
          <LayoutBody>
            <Paragraph color="darkergrey">
              Unable to load weather for this location. Try searching for
              another station.
            </Paragraph>
          </LayoutBody>
        ) : isForecastContentLoading ? (
          <LayoutBody>
            <WeatherForecastLoading />
          </LayoutBody>
        ) : outlookError && !hasOutlookDays ? (
          <LayoutBody>
            <MessageBlock
              type={outlookError.type === 'warning' ? 'warning' : 'error'}
              size="small"
            >
              {outlookError.message}
            </MessageBlock>
          </LayoutBody>
        ) : showDaySwipe ? (
          <DaySwipePager
            data-testid="weather-forecast-day-swipe"
            index={selectedDayIndex}
            pageCount={swipePageCount}
            onIndexChange={handleSwipeIndexChange}
            renderPage={renderSwipePage}
          />
        ) : null}
      </WeatherForecastPageLayout>
    </BasicDrawerLayout>
  );
};

export default WeatherForecastHomePage;
